/**
 * V2AdminDemosPage — /admin/os/demos
 * Create demo · list · copy link · send-email · delete
 *
 * Visual: shared OS dark cockpit (Chrome + C tokens) — card-per-demo rows,
 * no raw table, no cramping at 390.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Chrome, C } from './V2AdminOSPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'kindra-admin-token';
const auth = () => { const t = localStorage.getItem(TOKEN_KEY) || ''; return { headers: { Authorization: `Bearer ${t}` } }; };

const SURFACE = '#1A1150', LINE = '#2A2070', TEAL = '#34D1B0', MAGENTA = '#EB258D';
const btn = (bg, color = '#fff') => ({ background: bg, color, border: 'none', borderRadius: 999, padding: '8px 16px', fontFamily: 'Inter', fontWeight: 600, cursor: 'pointer', fontSize: 13 });
const ghost = { ...btn('transparent', C.muted), border: `1px solid ${C.hairBright}` };
const inputStyle = { padding: '10px 12px', border: `1px solid ${LINE}`, borderRadius: 8, fontFamily: 'Inter', fontSize: 14, background: C.navy, color: C.icy, outline: 'none' };

export default function V2AdminDemosPage() {
  const [industries, setIndustries] = useState([]);
  const [demos, setDemos] = useState([]);
  const [form, setForm] = useState({ business_name: '', industry_id: '', first_name: '', email: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [lastCreated, setLastCreated] = useState(null); // {business_name, demo_url}
  const [sendModal, setSendModal] = useState(null); // {demo, to_email}

  const load = async () => {
    try {
      const [i, d] = await Promise.all([
        axios.get(`${API}/admin/demos/industries`, auth()),
        axios.get(`${API}/admin/demos`, auth()),
      ]);
      setIndustries(i.data.industries || []);
      setDemos(d.data.demos || []);
      if (!form.industry_id && (i.data.industries || []).length) {
        setForm((f) => ({ ...f, industry_id: i.data.industries[0].id }));
      }
    } catch (e) { setToast('Load failed: ' + (e?.response?.data?.detail || e.message)); }
  };
  useEffect(() => { load(); }, []);

  const show = (m) => { setToast(m); setTimeout(() => setToast(''), 3200); };

  const create = async () => {
    if (!form.business_name.trim()) { show('Business name required'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/demos`, form, auth());
      show(`✓ Demo created — link copied`);
      setLastCreated({ business_name: form.business_name, demo_url: r.data.demo_url });
      try { await navigator.clipboard.writeText(r.data.demo_url); } catch (_) {}
      setForm({ business_name: '', industry_id: form.industry_id, first_name: '', email: '', notes: '' });
      load();
    } catch (e) { show('Create failed: ' + (e?.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const del = async (demo_id) => {
    if (!window.confirm('Delete this demo?')) return;
    try { await axios.delete(`${API}/admin/demos/${demo_id}`, auth()); load(); } catch (e) { show('Delete failed'); }
  };

  const copyLink = async (url) => { try { await navigator.clipboard.writeText(url); show('Link copied'); } catch (_) { show('Copy failed'); } };

  const doSend = async () => {
    if (!sendModal.to_email) return;
    try {
      await axios.post(`${API}/admin/demos/${sendModal.demo.demo_id}/send_email`, { to_email: sendModal.to_email, confirm: true }, auth());
      show(`✓ Demo email sent to ${sendModal.to_email}`);
      setSendModal(null); load();
    } catch (e) { show('Send failed: ' + (e?.response?.data?.detail || e.message)); }
  };

  return (
    <Chrome activeTab="demos">
      <div data-testid="admin-demos-page">
        <div style={{ marginBottom: 24 }}>
          <p style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', color: C.faint }}>
            Kindra OS · Demo links
          </p>
          <h1 style={{ fontFamily: 'Poppins,Inter,sans-serif', color: C.icy, fontSize: 'clamp(24px,3vw,30px)', fontWeight: 600, marginTop: 4 }}>Demo links</h1>
          <p style={{ fontFamily: 'Inter', color: C.muted, fontSize: 13, marginTop: 6, maxWidth: 640 }}>
            Type a business name + industry and Kindra spins up a shareable Leak Check demo. Send the link to prospects — nothing is sent to a real business until you send it yourself.
          </p>
        </div>

        {/* Create form */}
        <div style={{ background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 12, padding: 20, marginBottom: 24 }} data-testid="demo-create-form">
          <p style={{ fontFamily: 'Poppins,Inter,sans-serif', fontWeight: 600, color: C.icy, marginBottom: 12 }}>+ Create demo</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            <input data-testid="demo-input-business" placeholder="Business name *" value={form.business_name}
                    onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                    style={inputStyle} />
            <select data-testid="demo-select-industry" value={form.industry_id}
                    onChange={(e) => setForm({ ...form, industry_id: e.target.value })}
                    style={inputStyle}>
              {industries.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
            <input data-testid="demo-input-first-name" placeholder="First name (optional)" value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    style={inputStyle} />
            <input data-testid="demo-input-email" placeholder="Email (optional — auto-attaches Lead)" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    style={inputStyle} />
          </div>
          <input data-testid="demo-input-notes" placeholder="Notes (optional)" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  style={{ ...inputStyle, marginTop: 10, width: '100%' }} />
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <button data-testid="demo-create-submit" onClick={create} disabled={busy || !form.business_name.trim()}
                    style={{ ...btn(C.spectrum, '#fff'), fontWeight: 700, opacity: busy || !form.business_name.trim() ? 0.6 : 1 }}>
              {busy ? 'Creating…' : 'Create demo + copy link'}
            </button>
          </div>
          {lastCreated && (
            <div data-testid="demo-created-link" style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(52,209,176,0.10)', border: `1px solid ${TEAL}55`, borderRadius: 8, fontFamily: 'Inter', fontSize: 13, color: C.icy, wordBreak: 'break-all' }}>
              ✓ Demo for <b>{lastCreated.business_name}</b> is live:{' '}
              <a href={lastCreated.demo_url} target="_blank" rel="noopener noreferrer" style={{ color: '#5FE0BE', fontFamily: 'monospace' }}>{lastCreated.demo_url}</a>
              <span style={{ color: C.faint }}> (also copied to your clipboard)</span>
            </div>
          )}
        </div>

        {/* List — card per demo, no raw table, no cramping at 390 */}
        <div data-testid="demo-list" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {demos.map((d) => (
            <div key={d.demo_id} data-testid={`demo-row-${d.demo_id}`}
                 style={{ background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 12, padding: '14px 16px' }}>
              {/* Top row — identity left, viewed chip right */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                  <span style={{ color: C.icy, fontWeight: 700, fontSize: 14, fontFamily: 'Inter' }}>{d.business_name}</span>
                  <p style={{ fontFamily: 'Inter', fontSize: 11, color: C.faint, marginTop: 2, overflowWrap: 'anywhere' }}>
                    {d.first_name || '—'} · {d.email || 'no email'}
                  </p>
                </div>
                <span style={{ flex: '0 0 auto', background: d.view_count > 0 ? 'rgba(52,209,176,0.16)' : 'rgba(220,240,245,0.07)', color: d.view_count > 0 ? '#5FE0BE' : C.faint, padding: '4px 10px', borderRadius: 999, fontWeight: 700, fontSize: 12, fontFamily: 'Inter' }}>
                  {d.view_count > 0 ? `✓ ${d.view_count} view${d.view_count > 1 ? 's' : ''}` : 'Not viewed'}
                </span>
              </div>
              {/* Meta line */}
              <p style={{ margin: '7px 0 0', fontSize: 12, color: C.muted, fontFamily: 'Inter', overflowWrap: 'anywhere' }}>
                {d.industry_name} · created {(d.created_at || '').slice(0, 16).replace('T', ' ')} · {d.chat_message_count || 0} Qs asked
              </p>
              {/* Actions */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                <button onClick={() => copyLink(d.demo_url)} data-testid={`demo-copy-${d.demo_id}`}
                        style={{ ...ghost, padding: '5px 11px', fontSize: 11 }}>📋 Copy</button>
                <a href={d.demo_url} target="_blank" rel="noopener noreferrer" data-testid={`demo-open-${d.demo_id}`}
                   style={{ ...ghost, padding: '5px 11px', fontSize: 11, textDecoration: 'none', display: 'inline-block' }}>Open ↗</a>
                <button onClick={() => setSendModal({ demo: d, to_email: d.email || '' })} data-testid={`demo-send-${d.demo_id}`}
                        style={{ ...btn(TEAL, C.navy), padding: '5px 11px', fontSize: 11, fontWeight: 700 }}>✉ Send</button>
                <button onClick={() => del(d.demo_id)} data-testid={`demo-del-${d.demo_id}`}
                        style={{ ...btn('transparent', '#FF7DB3'), border: `1px solid ${MAGENTA}44`, padding: '5px 11px', fontSize: 11 }}>Delete</button>
              </div>
            </div>
          ))}
          {demos.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: C.faint, fontFamily: 'Inter', background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 12 }}>
              No demos yet — create one above.
            </div>
          )}
        </div>
      </div>

      {sendModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(16,8,56,0.78)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 20 }}
             data-testid="demo-send-modal">
          <div style={{ background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 16, padding: 24, maxWidth: 460, width: '100%' }}>
            <p style={{ fontFamily: 'Poppins,Inter,sans-serif', fontWeight: 600, fontSize: 18, color: C.icy, marginBottom: 8 }}>Send demo email</p>
            <p style={{ fontFamily: 'Inter', fontSize: 13, color: C.muted, marginBottom: 14 }}>Sends the personalised demo link to the recipient. Real email — Michael's click gate + suppression respected.</p>
            <input value={sendModal.to_email} onChange={(e) => setSendModal({ ...sendModal, to_email: e.target.value })}
                   data-testid="demo-send-modal-email"
                   placeholder="recipient@example.com"
                   style={{ ...inputStyle, width: '100%', marginBottom: 14 }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setSendModal(null)} style={ghost}>Cancel</button>
              <button data-testid="demo-send-modal-confirm" onClick={doSend} disabled={!sendModal.to_email} style={{ ...btn(MAGENTA), opacity: sendModal.to_email ? 1 : 0.5 }}>Send demo email</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div data-testid="demo-toast" style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: SURFACE, border: `1px solid ${LINE}`, color: C.icy, padding: '12px 20px', borderRadius: 999, fontFamily: 'Inter', fontSize: 13, boxShadow: '0 12px 40px rgba(0,0,0,0.5)', zIndex: 70 }}>{toast}</div>}
    </Chrome>
  );
}
